<template>
    <div v-if="shouldShowPicker" class="sprint-picker-wrapper mb-3 w-100" data-test="picker-sprints">
        <b-card v-if="hasRepoAvailable" no-body class="shadow-sm border py-2 px-3 sprint-scope-card w-100">
            <div class="d-flex flex-wrap justify-content-between align-items-center">
                <div class="sprint-scope-header mb-2 mb-lg-0 mr-lg-3 d-flex align-items-center">
                    <h6 class="font-weight-bold mb-0 sprint-scope-title d-inline-flex align-items-center">
                        <i class="bi bi-filter-square mr-2 text-primary"></i>
                        <span>{{ scopeTitle }}</span>
                    </h6>
                    <b-button
                        size="sm"
                        :variant="showScopeInfo ? 'info' : 'outline-info'"
                        class="btn-scope-info-toggle ml-2"
                        @click="toggleScopeInfo"
                        :aria-expanded="showScopeInfo ? 'true' : 'false'"
                        aria-label="Toggle sprint scope explanation"
                        v-b-tooltip.hover.bottom="showScopeInfo ? 'Hide sprint scope description' : 'Show sprint scope description'"
                        data-test="btn-toggle-scope-info"
                    >
                        <i class="bi bi-info-circle-fill text-white" v-if="showScopeInfo"></i>
                        <i class="bi bi-info-circle" v-else></i>
                    </b-button>
                </div>
                <div class="sprint-scope-picker-col d-flex align-items-center justify-content-center flex-grow-1">
                    <div class="sprint-bar-container shadow-sm" :data-state="loadingSprints ? 'loading' : 'idle'">
                        <!-- Context Row (Project Space + All Time + Refresh; display: contents on desktop) -->
                        <div class="sprint-bar-context-row">
                            <!-- Project Space Section (integrated left pill) -->
                            <div v-if="showProjectPicker && configuredProjects.length > 0" class="sprint-project-section d-inline-flex align-items-center">
                                <!-- Single project space: static label with logos -->
                                <div v-if="configuredProjects.length === 1"
                                     class="sprint-project-label d-inline-flex align-items-center"
                                     :title="'Project Space: ' + (configuredProjects[0].projectSpaceName || configuredProjects[0].projectName)"
                                     data-test="label-active-project">
                                    <i class="bi bi-folder2-open text-warning mr-1"></i>
                                    <span class="sprint-project-name text-truncate">{{ configuredProjects[0].projectSpaceName || configuredProjects[0].projectName }}</span>
                                    <span class="sprint-platform-logos d-inline-flex align-items-center ml-1">
                                        <i v-for="plat in getProjectPlatforms(configuredProjects[0])"
                                           :key="plat.type"
                                           :class="[plat.icon, 'platform-logo-icon', plat.brandClass]"
                                           :title="plat.name"
                                           class="ml-1"></i>
                                    </span>
                                </div>

                                <!-- Multi-project space: dropdown selector -->
                                <b-dropdown
                                    v-else
                                    id="sprintProjectDropdown"
                                    data-test="dropdown-project-selector"
                                    size="sm"
                                    :variant="userTheme === 'dark' ? 'dark' : 'light'"
                                    no-caret
                                    class="sprint-project-dropdown"
                                    toggle-class="sprint-project-dropdown-toggle d-flex align-items-center"
                                    menu-class="sprint-project-dropdown-menu shadow"
                                >
                                    <template #button-content>
                                        <i class="bi bi-folder2-open text-warning mr-1"></i>
                                        <span class="sprint-project-name text-truncate" :title="selectedProject ? (selectedProject.projectSpaceName || selectedProject.projectName) : 'Select Project Space'">
                                            {{ selectedProject ? (selectedProject.projectSpaceName || selectedProject.projectName) : 'Select Project Space' }}
                                        </span>
                                        <span class="sprint-platform-logos d-inline-flex align-items-center ml-1" v-if="selectedProject">
                                            <i v-for="plat in getProjectPlatforms(selectedProject)"
                                               :key="plat.type"
                                               :class="[plat.icon, 'platform-logo-icon', plat.brandClass]"
                                               :title="plat.name"
                                               class="ml-1"></i>
                                        </span>
                                        <i class="bi bi-chevron-down ml-1 sprint-project-chevron"></i>
                                    </template>
                                    <b-dropdown-header class="small font-weight-bold">Select Active Project Space</b-dropdown-header>
                                    <b-dropdown-item-button
                                        v-for="p in configuredProjects"
                                        :key="p.id"
                                        :data-test="`project-option-${p.id}`"
                                        :active="p.id === selectedProjectId || (selectedProject && p.id === selectedProject.id)"
                                        @click="onProjectSelected(p.id)"
                                        class="sprint-project-item"
                                    >
                                        <div class="d-flex align-items-center justify-content-between w-100">
                                            <div class="d-flex align-items-center text-truncate mr-3">
                                                <i v-if="p.id === selectedProjectId || (selectedProject && p.id === selectedProject.id)" class="bi bi-check2 text-success mr-2 font-weight-bold"></i>
                                                <span v-else style="display: inline-block; width: 1rem;" class="mr-2"></span>
                                                <span class="text-truncate font-weight-500">{{ p.projectSpaceName || p.projectName }}</span>
                                                <span v-if="p.isPrimary" class="text-warning ml-1" title="Primary Project Space">★</span>
                                            </div>
                                            <div class="sprint-platform-logos d-flex align-items-center flex-shrink-0">
                                                <i v-for="plat in getProjectPlatforms(p)"
                                                   :key="plat.type"
                                                   :class="[plat.icon, 'platform-logo-icon', plat.brandClass]"
                                                   :title="plat.name"
                                                   class="ml-1"></i>
                                            </div>
                                        </div>
                                    </b-dropdown-item-button>
                                </b-dropdown>
                            </div>

                            <div v-if="showProjectPicker && configuredProjects.length > 0" class="sprint-divider-line sprint-divider-project"></div>

                            <!-- All Time Button for Branch Manager -->
                            <button v-if="showAllTime"
                                    type="button"
                                    class="sprint-btn all-time-btn"
                                    :class="{ 'selected-sprint-button': isAllTimeSelected }"
                                    @click="pickAllTime"
                                    aria-label="Show all items across all time"
                                    v-b-tooltip.hover.bottom="'Show all items across all time'"
                                    :data-locked="isAllTimeLocked ? 'true' : 'false'"
                                    data-test="btn-sprint-all-time">
                                <div class="sprint-btn-name">
                                    <i v-if="isAllTimeLocked" class="bi bi-lock-fill text-warning mr-1" title="Locked on Free Plan"></i>
                                    <i v-else class="bi bi-infinity mr-1"></i>
                                    <span>All Time</span>
                                </div>
                                <div class="sprint-btn-dates">All items</div>
                            </button>

                            <div v-if="showAllTime && visibleSprints.length" class="sprint-divider-line sprint-divider-all-time"></div>

                            <div class="sprint-divider-line sprint-divider-refresh"></div>

                            <!-- Manual Refresh Sprints Button -->
                            <button type="button"
                                    class="sprint-nav-btn sprint-refresh-btn"
                                    :disabled="loadingSprints"
                                    @click="refreshSprints"
                                    aria-label="Refresh sprints from DevOps"
                                    v-b-tooltip.hover.bottom="'Refresh sprints from Azure DevOps'"
                                    data-test="btn-refresh-sprints">
                                <i class="bi bi-arrow-clockwise" :class="{ 'spin-icon': loadingSprints }"></i>
                            </button>
                        </div>

                        <!-- Inline loading state when no sprints are rendered yet -->
                        <div v-if="loadingSprints && !visibleSprints.length" class="sprint-loading-section d-inline-flex align-items-center px-3 py-1">
                            <b-spinner small variant="primary" class="mr-2"></b-spinner>
                            <span class="small text-muted font-weight-500">Loading sprints...</span>
                        </div>

                        <!-- Sprints Pills Section with subtle overlay during page changes -->
                        <b-overlay v-else-if="visibleSprints.length" :show="loadingSprints" rounded="sm" opacity="0.6" class="sprints-list-overlay d-inline-flex align-items-center justify-content-center">
                            <button v-if="totalPages > 1"
                                    type="button"
                                    class="sprint-nav-btn"
                                    :disabled="currentPage <= 1 || isViewOnlyMode"
                                    @click="goToPage(currentPage - 1)"
                                    aria-label="Older sprints"
                                    v-b-tooltip.hover.bottom="'Older sprints'"
                                    :data-locked="isViewOnlyMode ? 'true' : 'false'"
                                    data-test="btn-sprint-prev">
                                <i v-if="isViewOnlyMode" class="bi bi-lock-fill text-warning"></i>
                                <i v-else class="bi bi-chevron-left"></i>
                            </button>

                            <div class="sprint-scroll-wrapper"
                                 :class="{ 'can-scroll-left': canScrollLeft, 'can-scroll-right': canScrollRight }">
                                <button v-if="canScrollLeft"
                                        type="button"
                                        class="sprint-scroll-arrow left"
                                        @click="scrollSprintList(-140)"
                                        aria-label="Scroll sprints left"
                                        v-b-tooltip.hover.bottom="'Scroll sprints left'"
                                        data-test="btn-sprint-scroll-left">
                                    <i class="bi bi-chevron-left"></i>
                                </button>
                                <div ref="sprintList"
                                     class="sprint-buttons-list"
                                     @scroll="updateScrollIndicators">
                                    <button ref="sprint-buttons"
                                            v-for="sprint in visibleSprints"
                                            :key="sprint.id"
                                            type="button"
                                            @click="sprintPicked(sprint.path)"
                                            :aria-label="`Sprint ${sprint.name}`"
                                            :title="`Sprint ${sprint.name}`"
                                            data-test="btn-sprint-pill"
                                            :data-test-id="sprint.id || sprint.name"
                                            :data-locked="sprint.isLocked ? 'true' : 'false'"
                                            :class="['sprint-btn', { 'selected-sprint-button': !isAllTimeSelected && selectedSprintPath === sprint.path }]">
                                        <div class="sprint-btn-name">
                                            <i v-if="sprint.isLocked" class="bi bi-lock-fill text-warning mr-1" title="Locked on Free Plan"></i>
                                            <i v-else-if="sprint.position === 0" class="bi bi-play-circle-fill text-warning mr-1" title="Current Active Sprint"></i>
                                            <span>{{ sprint.name }}</span>
                                        </div>
                                        <div class="sprint-btn-dates">{{ formatSprintDates(sprint) }}</div>
                                    </button>
                                </div>
                                <button v-if="canScrollRight"
                                        type="button"
                                        class="sprint-scroll-arrow right"
                                        @click="scrollSprintList(140)"
                                        aria-label="Scroll sprints right"
                                        v-b-tooltip.hover.bottom="'Scroll sprints right'"
                                        data-test="btn-sprint-scroll-right">
                                    <i class="bi bi-chevron-right"></i>
                                </button>
                            </div>

                            <button v-if="totalPages > 1"
                                    type="button"
                                    class="sprint-nav-btn"
                                    :disabled="currentPage >= totalPages"
                                    @click="goToPage(currentPage + 1)"
                                    aria-label="Newer sprints"
                                    v-b-tooltip.hover.bottom="'Newer sprints'"
                                    data-test="btn-sprint-next">
                                <i class="bi bi-chevron-right"></i>
                            </button>

                            <template #overlay>
                                <b-spinner small variant="primary" label="Loading sprints..."></b-spinner>
                            </template>
                        </b-overlay>

                        <!-- Empty sprints state inside container -->
                        <div v-else class="sprint-empty-msg px-3 py-1 small text-muted d-inline-flex align-items-center">
                            <i class="bi bi-info-circle mr-1"></i>
                            <span>No sprints found for selected project.</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Collapsible description / explanation -->
            <b-collapse v-model="showScopeInfo" class="w-100">
                <div class="sprint-scope-info-alert mt-2 p-2 px-3 rounded small d-flex align-items-center justify-content-between">
                    <div class="d-flex align-items-center mr-2">
                        <i class="bi bi-info-circle-fill text-info mr-2 flex-shrink-0" style="font-size: 1rem;"></i>
                        <span>{{ effectiveScopeSubtitle }}</span>
                    </div>
                    <b-button-close
                        @click="showScopeInfo = false"
                        class="text-reset ml-2 flex-shrink-0"
                        aria-label="Dismiss sprint description"
                        v-b-tooltip.hover.bottom="'Dismiss description'"
                        data-test="btn-dismiss-scope-info"
                    />
                </div>
            </b-collapse>
        </b-card>
    </div>
</template>

<script>
    import _ from "lodash";
    import eventBus from "../event-bus";
    import accountMixin from "../mixinAccount";
    import toastMixin from "../mixinToast";

    export default {
        mixins: [accountMixin, toastMixin],
        props: {
            requireRepo: {
                type: Boolean,
                default: false
            },
            hideIfEmpty: {
                type: Boolean,
                default: false
            },
            connectionContext: {
                type: String,
                default: 'all',
                validator: val => ['all', 'backlog', 'repo'].includes(val)
            },
            showAllTime: {
                type: Boolean,
                default: false
            },
            showProjectPicker: {
                type: Boolean,
                default: true
            },
            scopeTitle: {
                type: String,
                default: "Sprint Scope"
            },
            scopeSubtitle: {
                type: String,
                default: ""
            }
        },
        data() {
            return {
                selectedSprintPath: null,
                isAllTimeSelected: false,
                loadingSprints: false,
                currentPage: this.$store?.state?.lastSprintsPage || 1,
                totalPages: this.$store?.state?.lastSprintsTotalPages || 1,
                isAllTimeLocked: false,
                canScrollLeft: false,
                canScrollRight: false,
                showScopeInfo: false
            };
        },
        watch: {
            selectedProjectId: {
                async handler() {
                    this.selectedSprintPath = null;
                    this.isAllTimeSelected = false;
                    await this.loadSprintsAsync();
                }
            },
            projectConfigSignature: {
                async handler(newSig, oldSig) {
                    if (oldSig && newSig !== oldSig) {
                        this.selectedSprintPath = null;
                        this.isAllTimeSelected = false;
                        await this.loadSprintsAsync(null, true);
                    }
                }
            },
            visibleSprints() {
                this.updateScrollIndicators();
            }
        },
        computed: {
            effectiveScopeSubtitle() {
                if (this.scopeSubtitle) return this.scopeSubtitle;
                if (this.connectionContext === 'repo') {
                    return 'Select an active Azure DevOps sprint to analyze branches, pull requests, and code reviews.';
                }
                return 'Select an active Azure DevOps sprint to analyze capacity burn-down, deliverables, and leave schedules.';
            },
            sprints() {
                return this.$store.state.sprints || [];
            },
            lastPickedSprint() {
                return this.$store.state.lastPickedSprint;
            },
            selectedProjectId() {
                return this.globalProjectId;
            },
            selectedProject() {
                return this.globalProject;
            },
            currentSprint() {
                return _.find(this.sprints, s => s.position === 0) || null;
            },
            visibleSprints() {
                return this.sprints || [];
            },
            shouldShowPicker() {
                if (!this.selectedProject && (!this.configuredProjects || !this.configuredProjects.length)) return false;
                if (this.requireRepo && this.selectedProject && !this.selectedProject.repoLinkId) {
                    return false;
                }
                if (!this.selectedProject || !this.selectedProject.backlogLinkId || !this.selectedProject.backlogProjectId) {
                    // Show if multiple projects can be selected, otherwise false
                    return this.showProjectPicker && this.configuredProjects.length > 1;
                }
                return true;
            },
            hasRepoAvailable() {
                return !this.requireRepo || (this.selectedProject && !!this.selectedProject.repoLinkId);
            },
            projectConfigSignature() {
                if (!this.selectedProject) return '';
                return `${this.selectedProject.id || ''}_${this.selectedProject.backlogProjectId || ''}_${this.selectedProject.backlogTeamId || ''}_${this.selectedProject.backlogTeamName || ''}`;
            }
        },
        async created() {
            await this.loadSprintsAsync();
            this.$root.$on('plan-upgrade-required', this.onPlanUpgradeRequired);
            eventBus.$on('project-space-config-changed', this.onProjectSpaceConfigChanged);
            eventBus.$on('global-project-changed', this.onGlobalProjectChanged);
        },
        mounted() {
            window.addEventListener('resize', this.updateScrollIndicators);
            this.updateScrollIndicators();
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.updateScrollIndicators);
            this.$root.$off('plan-upgrade-required', this.onPlanUpgradeRequired);
            eventBus.$off('project-space-config-changed', this.onProjectSpaceConfigChanged);
            eventBus.$off('global-project-changed', this.onGlobalProjectChanged);
        },
        methods: {
            async loadSprintsAsync(targetPage = null, force = false) {
                if (this.connectionContext === 'backlog' && !this.hasBacklogSetup) {
                    this.$store.commit('clearSprints');
                    this.loadingSprints = false;
                    this.$emit('sprint-picked', '', null, this.selectedProject);
                    this.$emit('sprints-loaded', []);
                    return;
                }
                if (!this.selectedProject) {
                    this.$store.commit('clearSprints');
                    this.$emit('sprint-picked', '', null, null);
                    this.$emit('sprints-loaded', []);
                    return;
                }
                if (!this.selectedProject.backlogLinkId || !this.selectedProject.backlogProjectId) {
                    this.$store.commit('clearSprints');
                    this.loadingSprints = false;
                    this.$emit('sprint-picked', '', null, this.selectedProject);
                    this.$emit('sprints-loaded', []);
                    return;
                }
                if (this.requireRepo && !this.selectedProject.repoLinkId) {
                    this.$store.commit('clearSprints');
                    this.loadingSprints = false;
                    this.$emit('sprint-picked', '', null, this.selectedProject);
                    this.$emit('sprints-loaded', []);
                    return;
                }

                const targetProjectId = this.selectedProject.id || this.selectedProject.backlogProjectId || this.selectedProject.projectId;
                const isCached = !force
                    && this.$store.state.lastSprintsProjectId === targetProjectId
                    && this.sprints
                    && this.sprints.length > 0
                    && (targetPage === null || targetPage === this.$store.state.lastSprintsPage);

                if (!isCached) {
                    this.loadingSprints = true;
                }

                try {
                    let savedPath = null;
                    if (targetPage === null) {
                        savedPath = this.getSavedSprintForProject();
                        if (!this.isSoloOrHigher) {
                            savedPath = null;
                        }
                    }

                    const result = await this.$store.dispatch('fetchSprintsAsync', {
                        projectId: targetProjectId,
                        page: targetPage,
                        sprintPath: savedPath,
                        force: Boolean(force)
                    });

                    if (result) {
                        this.currentPage = result.page || 1;
                        this.totalPages = result.totalPages || 1;
                        this.isAllTimeLocked = Boolean(result.isAllTimeLocked);
                    }
                } catch (err) {
                    if (targetPage === null) {
                        try {
                            this.saveSprintForProject(null);
                            const fallbackResult = await this.$store.dispatch('fetchSprintsAsync', {
                                projectId: targetProjectId,
                                force: true
                            });
                            if (fallbackResult) {
                                this.currentPage = fallbackResult.page || 1;
                                this.totalPages = fallbackResult.totalPages || 1;
                            }
                        } catch (retryErr) {
                            // ignore retry error
                        }
                    }
                    if (err?.status !== 402 && !err?.isPlanUpgrade) {
                        console.error('Failed to load sprints:', err);
                    }
                } finally {
                    this.loadingSprints = false;
                }
                this.$emit('sprints-loaded', this.sprints || []);
                this.selectInitialSprint();
                this.updateScrollIndicators();
            },
            getSavedSprintForProject() {
                if (this.selectedProjectId) {
                    const val = localStorage.getItem('velocytrack_last_sprint_' + this.selectedProjectId);
                    if (val) return val;
                }
                return this.$store.state.lastPickedSprint || localStorage.getItem('velocytrack_last_sprint') || null;
            },
            saveSprintForProject(sprintPath) {
                this.$store.commit('setSprintPick', sprintPath);
                if (this.selectedProjectId) {
                    localStorage.setItem('velocytrack_last_sprint_' + this.selectedProjectId, sprintPath);
                }
                localStorage.setItem('velocytrack_last_sprint', sprintPath);
            },
            selectInitialSprint() {
                if (this.isAllTimeSelected) {
                    return;
                }
                if (!this.sprints || this.sprints.length === 0) {
                    this.selectedSprintPath = '';
                    this.$emit('sprint-picked', '', null, this.selectedProject);
                    return;
                }

                // If currently selected sprint is already present on this page, keep it
                if (this.selectedSprintPath && _.some(this.sprints, s => s.path === this.selectedSprintPath)) {
                    return;
                }

                const savedPath = this.getSavedSprintForProject();
                let targetSprint = null;

                if (savedPath) {
                    const match = _.find(this.sprints, s => s.path === savedPath);
                    if (match && !match.isLocked) {
                        targetSprint = match;
                    } else {
                        // Stale or locked saved sprint - clear it
                        this.saveSprintForProject(null);
                    }
                }

                // If no valid saved sprint, default to current active sprint (position 0)
                if (!targetSprint) {
                    targetSprint = _.find(this.sprints, s => s.position === 0);
                }

                // Fallback to first non-locked sprint on the page, or first sprint
                if (!targetSprint) {
                    targetSprint = _.find(this.sprints, s => !s.isLocked) || this.sprints[0];
                }

                if (targetSprint) {
                    this.sprintPicked(targetSprint.path);
                }
            },
            async goToPage(page) {
                if (this.isViewOnlyMode && page < this.totalPages) {
                    try {
                        await this.$http.get('/Azure/ListSprints', { params: { page } });
                    } catch (e) {
                        // Handled by 402 interceptor
                    }
                    return;
                }
                if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
                    try {
                        await this.loadSprintsAsync(page);
                    } catch (e) {
                        // Handled by 402 interceptor / loadSprintsAsync
                    }
                }
            },
            syncSelectedSprint() {
                const savedSprint = this.getSavedSprintForProject();
                if (savedSprint && _.some(this.sprints, s => s.path === savedSprint && !s.isLocked)) {
                    this.selectedSprintPath = savedSprint;
                } else if (!this.selectedSprintPath) {
                    this.selectInitialSprint();
                }
            },
            async pickAllTime() {
                if (this.isAllTimeLocked) {
                    try {
                        await this.$http.get('/Azure/ListBranches', { params: { allTime: true } });
                    } catch (e) {
                        // Handled by 402 interceptor which opens plan upgrade modal
                    }
                    return;
                }
                this.isAllTimeSelected = true;
                this.selectedSprintPath = null;
                this.$emit('all-time-picked', true);
                this.$emit('sprint-picked', '', null, this.selectedProject, true);
            },
            async sprintPicked(sprintPath) {
                const rawSprint = _.find(this.sprints, s => s.path === sprintPath) || null;
                if (rawSprint?.isLocked) {
                    try {
                        await this.$http.get('/Azure/ListPullRequests', { params: { sprint: sprintPath } });
                    } catch (e) {
                        // Handled by 402 interceptor which opens plan upgrade modal
                    }
                    return;
                }
                this.isAllTimeSelected = false;
                this.saveSprintForProject(sprintPath);
                this.selectedSprintPath = sprintPath;
                const sprint = rawSprint ? { ...rawSprint } : null;
                if (sprint) {
                    const sorted = [...this.sprints].sort((a, b) => new Date(a.from || 0) - new Date(b.from || 0));
                    const idx = sorted.findIndex(s => s.path === sprintPath);
                    if (idx >= 0 && idx < sorted.length - 1 && sorted[idx + 1]?.from) {
                        const nextFrom = new Date(sorted[idx + 1].from);
                        if (!isNaN(nextFrom.getTime()) && nextFrom.getFullYear() >= 2000) {
                            sprint.to = sorted[idx + 1].from;
                        }
                    }
                    if (sprint.from) {
                        const fromD = new Date(sprint.from);
                        const toD = sprint.to ? new Date(sprint.to) : null;
                        if (!isNaN(fromD.getTime()) && fromD.getFullYear() >= 2000) {
                            if (!toD || isNaN(toD.getTime()) || toD.getFullYear() < 2000) {
                                const endD = new Date(fromD.getTime() + 14 * 24 * 60 * 60 * 1000);
                                sprint.to = endD.toISOString();
                            }
                        }
                    }
                }
                try {
                    this.$emit('all-time-picked', false);
                    this.$emit('sprint-picked', sprintPath, sprint, this.selectedProject, false);
                } catch (e) {
                    // Suppress unhandled event listener errors
                }
                this.updateScrollIndicators();
            },
            async onPlanUpgradeRequired() {
                // Silently revert the visual selection to current sprint without re-emitting @sprint-picked
                if (this.currentSprint && this.selectedSprintPath !== this.currentSprint.path) {
                    this.selectedSprintPath = this.currentSprint.path;
                    this.saveSprintForProject(this.currentSprint.path);
                } else if (!this.currentSprint && this.currentPage !== this.totalPages) {
                    // Current sprint is on the last page: request without page parameter from BE
                    await this.loadSprintsAsync();
                }
            },
            formatSprintDates(sprint) {
                const from = sprint.from ? new Date(sprint.from) : null;
                if (!from || from.getFullYear() < 2000) return '';
                const opts = { day: '2-digit', month: 'short' };
                return from.toLocaleDateString('en-GB', opts);
            },
            onProjectSelected(newProjId) {
                if (newProjId === this.selectedProjectId) return;
                this.selectedSprintPath = null;
                this.isAllTimeSelected = false;
                this.$store.commit('setSelectedProjectId', newProjId);
                try {
                    localStorage.setItem('velocytrack_last_project_id', newProjId);
                } catch (e) {
                    // ignore
                }
                eventBus.$emit('global-project-changed', newProjId);
            },
            async onProjectSpaceConfigChanged(projId) {
                if (!projId || !this.selectedProjectId || String(projId) === String(this.selectedProjectId)) {
                    this.selectedSprintPath = null;
                    this.isAllTimeSelected = false;
                    await this.loadSprintsAsync(null, true);
                }
            },
            async onGlobalProjectChanged(projId) {
                if (projId && String(projId) === String(this.selectedProjectId)) {
                    return;
                }
                this.selectedSprintPath = null;
                this.isAllTimeSelected = false;
                await this.loadSprintsAsync();
            },
            async refreshSprints() {
                if (this.loadingSprints) return;
                try {
                    const targetPage = this.isSoloOrHigher ? this.currentPage : null;
                    await this.loadSprintsAsync(targetPage, true);
                    if (this.selectedSprintPath && _.some(this.sprints, s => s.path === this.selectedSprintPath)) {
                        await this.sprintPicked(this.selectedSprintPath);
                    }
                    this.$emit('sprints-refreshed');
                    this.toastSuccess('Sprints Refreshed', 'Fetched latest sprints from Azure DevOps.');
                } catch (e) {
                    if (e?.status !== 402 && !e?.isPlanUpgrade) {
                        this.toastError('Refresh Failed', 'Unable to fetch sprints from Azure DevOps.');
                    }
                }
            },
            updateScrollIndicators() {
                this.$nextTick(() => {
                    const el = this.$refs.sprintList;
                    if (!el) {
                        this.canScrollLeft = false;
                        this.canScrollRight = false;
                        return;
                    }
                    this.canScrollLeft = el.scrollLeft > 4;
                    this.canScrollRight = el.scrollLeft < (el.scrollWidth - el.clientWidth - 4);
                });
            },
            scrollSprintList(delta) {
                const el = this.$refs.sprintList;
                if (el) {
                    el.scrollBy({ left: delta, behavior: 'smooth' });
                }
            },
            toggleScopeInfo() {
                this.showScopeInfo = !this.showScopeInfo;
            }
        }
    };
</script>

<style scoped>
.sprint-scope-picker-col {
    max-width: 100%;
}

.btn-scope-info-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    width: 22px;
    padding: 0;
    font-size: 0.8rem;
    border-radius: 6px;
    transition: all 0.15s ease;
    line-height: 1;
}

.sprint-scope-info-alert {
    background-color: rgba(2, 132, 199, 0.08);
    border: 1px solid rgba(2, 132, 199, 0.2);
    color: var(--vt-text, #334155);
}

[data-theme="dark"] .sprint-scope-info-alert {
    background-color: rgba(2, 132, 199, 0.15);
    border-color: rgba(56, 189, 248, 0.25);
    color: #e2e8f0;
}

.sprint-bar-container {
    justify-content: center;
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    background-color: #f1f5f9;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 3px 4px;
    transition: background-color 0.2s ease, border-color 0.2s ease;
}

[data-theme="dark"] .sprint-bar-container {
    justify-content: center;
    background-color: #1e293b;
    border-color: #334155;
}

.sprint-bar-context-row {
    display: contents;
}

.sprint-divider-line {
    width: 1px;
    height: 24px;
    background-color: #cbd5e1;
    margin: 0 4px;
    flex-shrink: 0;
}

[data-theme="dark"] .sprint-divider-line {
    background-color: rgba(255, 255, 255, 0.2);
}

.all-time-btn {
    min-width: 90px;
}

.sprint-scroll-wrapper {
    justify-content: center;
    position: relative;
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
}

/* Gradient fade masks on edges when scrollable */
.sprint-scroll-wrapper::before,
.sprint-scroll-wrapper::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 22px;
    pointer-events: none;
    z-index: 2;
    opacity: 0;
    transition: opacity 0.2s ease;
}

.sprint-scroll-wrapper::before {
    left: 0;
    background: linear-gradient(to right, #f1f5f9 20%, transparent);
}

.sprint-scroll-wrapper::after {
    right: 0;
    background: linear-gradient(to left, #f1f5f9 20%, transparent);
}

[data-theme="dark"] .sprint-scroll-wrapper::before {
    background: linear-gradient(to right, #1e293b 20%, transparent);
}

[data-theme="dark"] .sprint-scroll-wrapper::after {
    background: linear-gradient(to left, #1e293b 20%, transparent);
}

.sprint-scroll-wrapper.can-scroll-left::before {
    opacity: 1;
}

.sprint-scroll-wrapper.can-scroll-right::after {
    opacity: 1;
}

.sprint-scroll-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 3;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: #ffffff;
    color: #475569;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
    padding: 0;
    transition: all 0.15s ease;
}

.sprint-scroll-arrow:hover {
    background: #0284c7;
    color: #ffffff;
    border-color: #0284c7;
}

.sprint-scroll-arrow.left {
    left: 2px;
}

.sprint-scroll-arrow.right {
    right: 2px;
}

[data-theme="dark"] .sprint-scroll-arrow {
    background: #0f172a;
    color: #cbd5e1;
    border-color: #475569;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .sprint-scroll-arrow:hover {
    background: #38bdf8;
    color: #0f172a;
    border-color: #38bdf8;
}

.sprint-buttons-list {
    justify-content: center;
    margin: 0 auto;
    display: inline-flex;
    align-items: center;
    overflow-x: auto;
    max-width: 100%;
    scroll-behavior: smooth;
    scrollbar-width: thin;
    scrollbar-color: rgba(56, 189, 248, 0.45) transparent;
    padding-bottom: 2px;
}

.sprint-buttons-list::-webkit-scrollbar {
    height: 3px;
}

.sprint-buttons-list::-webkit-scrollbar-track {
    background: transparent;
}

.sprint-buttons-list::-webkit-scrollbar-thumb {
    background: rgba(100, 116, 139, 0.35);
    border-radius: 3px;
}

.sprint-buttons-list::-webkit-scrollbar-thumb:hover {
    background: rgba(2, 132, 199, 0.7);
}

[data-theme="dark"] .sprint-buttons-list::-webkit-scrollbar-thumb {
    background: rgba(56, 189, 248, 0.35);
}

[data-theme="dark"] .sprint-buttons-list::-webkit-scrollbar-thumb:hover {
    background: rgba(56, 189, 248, 0.7);
}

.sprint-btn {
    text-align: center;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    line-height: 1.2;
    padding: 6px 14px;
    min-width: 105px;
    border-radius: 6px;
    margin: 0 2px;
    background-color: transparent;
    border: none;
    color: #475569;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.sprint-btn:hover {
    background-color: #e2e8f0;
    color: #0f172a;
}

.sprint-btn.selected-sprint-button {
    background-color: #ffffff !important;
    color: #0284c7 !important;
    font-weight: bold;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1) !important;
}

[data-theme="dark"] .sprint-btn {
    text-align: center;
    color: rgba(255, 255, 255, 0.75);
}

[data-theme="dark"] .sprint-btn:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: #ffffff;
}

[data-theme="dark"] .sprint-btn.selected-sprint-button {
    background-color: #0284c7 !important;
    color: #ffffff !important;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4) !important;
}

.sprint-btn-name {
    font-size: 0.85rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
}

.sprint-btn-dates {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 0.7rem;
    opacity: 0.75;
    white-space: nowrap;
    margin-top: 2px;
}

.sprint-nav-btn {
    padding: 6px 8px;
    height: 100%;
    border-radius: 6px;
    background-color: transparent;
    border: none;
    color: #475569;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.sprint-nav-btn:hover:not(:disabled) {
    background-color: #e2e8f0;
    color: #0f172a;
}

.sprint-nav-btn:disabled {
    opacity: 0.35;
    cursor: not-allowed;
}

[data-theme="dark"] .sprint-nav-btn {
    color: rgba(255, 255, 255, 0.75);
}

[data-theme="dark"] .sprint-nav-btn:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.15);
    color: #ffffff;
}

[data-theme="dark"] .sprint-nav-btn:disabled {
    opacity: 0.25;
}

.sprints-list-overlay {
    justify-content: center;
    display: inline-flex;
    align-items: center;
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
}

.sprint-loading-section {
    font-size: 0.82rem;
    white-space: nowrap;
    padding: 6px 14px;
}

[data-theme="dark"] .sprint-loading-section {
    color: rgba(255, 255, 255, 0.75);
}

[data-theme="dark"] .sprint-loading-section .text-muted {
    color: rgba(255, 255, 255, 0.75) !important;
}

.sprint-bar-overlay {
    min-height: 38px;
    display: inline-block;
}

.sprint-bar-placeholder {
    height: 38px;
    min-width: 260px;
}

/* Project Space Trigger within Sprint Bar */
.sprint-project-section {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
}

.sprint-project-label {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    font-size: 0.82rem;
    font-weight: 500;
    color: #334155;
    white-space: nowrap;
}

[data-theme="dark"] .sprint-project-label {
    color: rgba(255, 255, 255, 0.85);
}

.sprint-project-name {
    max-width: 140px;
    font-weight: 600;
    font-size: 0.82rem;
}

.sprint-project-chevron {
    font-size: 0.72rem;
    opacity: 0.65;
    transition: transform 0.2s ease;
}

.sprint-project-dropdown {
    display: inline-flex;
}

.sprint-project-dropdown ::v-deep .sprint-project-dropdown-toggle {
    display: inline-flex;
    align-items: center;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    padding: 6px 10px;
    font-size: 0.82rem;
    font-weight: 600;
    color: #334155;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.sprint-project-dropdown ::v-deep .sprint-project-dropdown-toggle:hover,
.sprint-project-dropdown ::v-deep .sprint-project-dropdown-toggle:focus {
    background-color: #e2e8f0 !important;
    color: #0f172a !important;
}

[data-theme="dark"] .sprint-project-dropdown ::v-deep .sprint-project-dropdown-toggle {
    color: rgba(255, 255, 255, 0.85);
}

[data-theme="dark"] .sprint-project-dropdown ::v-deep .sprint-project-dropdown-toggle:hover,
[data-theme="dark"] .sprint-project-dropdown ::v-deep .sprint-project-dropdown-toggle:focus {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: #ffffff !important;
}

.sprint-project-dropdown ::v-deep .sprint-project-dropdown-menu {
    min-width: 240px;
    font-size: 0.82rem;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 6px 0;
}

[data-theme="dark"] .sprint-project-dropdown ::v-deep .sprint-project-dropdown-menu {
    background-color: #1e293b;
    border-color: #334155;
}

.sprint-project-dropdown ::v-deep .dropdown-item {
    font-size: 0.82rem;
    padding: 7px 14px;
}

[data-theme="dark"] .sprint-project-dropdown ::v-deep .dropdown-header {
    color: rgba(255, 255, 255, 0.5);
}

[data-theme="dark"] .sprint-project-dropdown ::v-deep .dropdown-item {
    color: rgba(255, 255, 255, 0.85);
}

[data-theme="dark"] .sprint-project-dropdown ::v-deep .dropdown-item:hover,
[data-theme="dark"] .sprint-project-dropdown ::v-deep .dropdown-item:focus {
    background-color: rgba(255, 255, 255, 0.08);
    color: #ffffff;
}

[data-theme="dark"] .sprint-project-dropdown ::v-deep .dropdown-item.active {
    background-color: rgba(2, 132, 199, 0.25);
    color: #38bdf8;
}

/* Platform branding badge icons */
.platform-logo-icon {
    font-size: 0.85rem;
}

.brand-azure { color: #0078d4; }
.brand-gitlab { color: #fc6d26; }
.brand-github { color: #24292e; }
.brand-jira { color: #0052cc; }

[data-theme="dark"] .brand-azure { color: #2b88d8; }
[data-theme="dark"] .brand-gitlab { color: #fc6d26; }
[data-theme="dark"] .brand-github { color: #f0f6fc; }
[data-theme="dark"] .brand-jira { color: #2684ff; }

.sprint-refresh-btn {
    padding: 6px 10px;
    font-size: 0.88rem;
    color: #475569;
    flex-shrink: 0;
}

.sprint-refresh-btn:hover:not(:disabled) {
    color: #0284c7;
}

[data-theme="dark"] .sprint-refresh-btn {
    color: rgba(255, 255, 255, 0.75);
}

[data-theme="dark"] .sprint-refresh-btn:hover:not(:disabled) {
    color: #38bdf8;
}

.spin-icon {
    animation: sprint-refresh-spin 0.8s linear infinite;
    display: inline-block;
}

@keyframes sprint-refresh-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.sprint-empty-msg {
    font-size: 0.78rem;
    font-style: italic;
    color: #64748b;
}

[data-theme="dark"] .sprint-empty-msg {
    color: #94a3b8;
}

/* Unified Sprint Scope Card */
.sprint-scope-card {
    background-color: var(--vt-card-bg, #ffffff) !important;
    border-color: var(--vt-border, #dee2e6) !important;
    border-radius: 12px;
}

.sprint-scope-title {
    color: var(--vt-text-primary, #212529);
    letter-spacing: -0.01em;
}

.sprint-scope-subtitle {
    color: var(--vt-muted, #6c757d);
    font-size: 0.85rem;
}

[data-theme="dark"] .sprint-scope-card {
    background-color: #1e293b !important;
    border-color: #334155 !important;
}

[data-theme="dark"] .sprint-scope-title {
    color: #f1f5f9 !important;
}

[data-theme="dark"] .sprint-scope-subtitle {
    color: #94a3b8 !important;
}

/* Responsive Two-Tier Layout for Mobile Phone screens (<576px) */
@media (min-width: 576px) {
    .sprint-bar-context-row {
        display: contents;
    }

    .sprint-project-section {
        order: 1;
    }

    .sprint-divider-project {
        order: 2;
    }

    .all-time-btn {
        order: 3;
    }

    .sprint-divider-all-time {
        order: 4;
    }

    .sprints-list-overlay,
    .sprint-loading-section,
    .sprint-empty-msg {
        order: 5;
    }

    .sprint-divider-refresh {
        order: 6;
        display: block;
    }

    .sprint-refresh-btn {
        order: 7;
    }
}

@media (min-width: 576px) and (max-width: 991.98px) {
    .sprint-scope-picker-col {
        width: 100%;
    }

    .sprint-bar-container {
        display: flex;
        width: 100%;
        justify-content: center;
    }

    .sprints-list-overlay {
        flex: 1 1 auto;
        justify-content: center;
    }
}

@media (max-width: 575.98px) {
    .sprint-scope-picker-col {
        width: 100%;
    }

    .sprint-bar-container {
    justify-content: center;
        display: flex;
        flex-direction: column;
        width: 100%;
        background-color: transparent !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0;
        gap: 6px;
    }

    .sprint-bar-context-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        background-color: #f1f5f9;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 3px 6px;
        min-height: 42px;
        box-sizing: border-box;
    }

    [data-theme="dark"] .sprint-bar-context-row {
        background-color: #1e293b;
        border-color: #334155;
    }

    .sprint-bar-context-row .sprint-project-section {
        flex: 1 1 auto;
        min-width: 0;
    }

    .sprint-bar-context-row .sprint-project-name {
        max-width: 130px;
    }

    .sprint-bar-context-row .all-time-btn {
        flex-shrink: 0;
        padding: 4px 8px;
        min-width: 76px;
    }

    .sprint-bar-context-row .sprint-divider-refresh {
        display: none !important;
    }

    .sprint-bar-context-row .sprint-refresh-btn {
        flex-shrink: 0;
        padding: 4px 8px;
    }

    .sprints-list-overlay {
        justify-content: center;
        display: flex;
        align-items: center;
        width: 100%;
        background-color: #f1f5f9;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 3px 4px;
        min-height: 44px;
        box-sizing: border-box;
    }

    [data-theme="dark"] .sprints-list-overlay {
    justify-content: center;
        background-color: #1e293b;
        border-color: #334155;
    }

    .sprints-list-overlay .sprint-scroll-wrapper {
    justify-content: center;
        flex: 1 1 100%;
        width: 100%;
        max-width: 100%;
        min-width: 0;
    }

    .sprints-list-overlay .sprint-buttons-list {
    justify-content: center;
    margin: 0 auto;
        width: 100%;
    }

    .sprints-list-overlay .sprint-btn {
    text-align: center;
        min-width: 95px;
        padding: 5px 10px;
    }

    .sprint-empty-msg,
    .sprint-loading-section {
        width: 100%;
        background-color: #f1f5f9;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 6px 12px;
        justify-content: center;
    }

    [data-theme="dark"] .sprint-empty-msg,
    [data-theme="dark"] .sprint-loading-section {
        background-color: #1e293b;
        border-color: #334155;
    }
}
</style>
